/* eslint-disable indent */
import { useEffect } from 'react';
import { atom, selector, selectorFamily, useRecoilState, useRecoilValue } from 'recoil';
import { globalFontSizeKeyAtom } from './theme';

// 定义标签页类型
interface TabItem {
  key: string;
  label: string;
  path: string;
  params?: string;
}

// 基础 atom
export const cacheTabAtom = atom<TabItem[]>({
  key: 'cacheTab',
  default: [],
});

// tab 缓存高度
export const cacheHeightAtom = atom<number>({
  key: 'cacheHeight',
  default: 0,
});

// 选择器：获取当前激活的标签
export const activeTabSelector = selector({
  key: 'activeTabSelector',
  get: ({ get }) => {
    const tabs = get(cacheTabAtom);
    return tabs.find((tab) => tab.key === window.location.pathname) || null;
  },
});

// 选择器：获取标签列表
export const tabListSelector = selector({
  key: 'tabListSelector',
  get: ({ get }) => {
    return get(cacheTabAtom);
  },
});

// 选择器：判断标签是否存在
export const isTabExistSelector = selectorFamily({
  key: 'isTabExistSelector',
  get:
    (path: string) =>
    ({ get }) => {
      const tabs = get(cacheTabAtom);
      return tabs.some((tab) => tab.key === path);
    },
});

// 自定义 Hook：管理标签页
export const useCacheTab = () => {
  const [tabs, setTabs] = useRecoilState(cacheTabAtom);
  const [cacheHeight, setCacheHeight] = useRecoilState(cacheHeightAtom);
  const globalFontSizeKey = useRecoilValue(globalFontSizeKeyAtom);

  useEffect(() => {
    if (tabs.length > 0) {
      if (globalFontSizeKey === '1') {
        setCacheHeight(42);
      } else if (globalFontSizeKey === '2') {
        setCacheHeight(44);
      } else if (globalFontSizeKey === '3') {
        setCacheHeight(43);
      }
    } else {
      setCacheHeight(0);
    }
  }, [tabs]);

  // 添加标签
  const addTab = (tab: TabItem) => {
    // 直接使用 tabs 判断，避免 selector 的问题
    const exists = tabs.some((t) => t.key === tab.key);
    if (!exists) {
      setTabs((prev) => [...prev, tab]);
    }
  };

  // 关闭标签
  const closeTab = (key: string) => {
    setTabs((prev) => prev.filter((tab) => tab.key !== key));
  };

  // 关闭其他标签
  const closeOtherTabs = (key: string) => {
    setTabs((prev) => prev.filter((tab) => tab.key === key));
  };

  // 关闭右侧标签
  const closeRightTabs = (key: string) => {
    const index = tabs.findIndex((tab) => tab.key === key);
    setTabs((prev) => prev.slice(0, index + 1));
  };

  // 关闭左侧标签
  const closeLeftTabs = (key: string) => {
    const index = tabs.findIndex((tab) => tab.key === key);
    setTabs((prev) => prev.slice(index));
  };

  // 更新标签
  const updateTab = (key: string, newTab: Partial<TabItem>) => {
    setTabs((prev) => prev.map((tab) => (tab.key === key ? { ...tab, ...newTab } : tab)));
  };

  // 清空所有标签
  const clearTabs = () => {
    setTabs([]);
  };

  return {
    cacheHeight,
    tabs,
    addTab,
    closeTab,
    closeOtherTabs,
    closeRightTabs,
    closeLeftTabs,
    updateTab,
    clearTabs,
    setCacheHeight,
  };
};
